import type { WhispSiteView } from '../../../model';
import { isWhispSettled, whispChipState } from './whisp-layer';

export const WHISP_POLL_MS = 10_000;
export const WHISP_POLL_SLOW_MS = 30_000;
export const WHISP_POLL_BACKOFF_AFTER_MS = 2 * 60_000;
export const WHISP_POLL_MAX_MS = 30 * 60_000;
const MAX_CONSECUTIVE_FAILURES = 3;

export interface WhispWatch {
  fetchView: () => Promise<WhispSiteView | null | undefined>;
  onUpdate: (view: WhispSiteView) => void;
  onError: () => void;
}

export function watchWhisp({ fetchView, onUpdate, onError }: WhispWatch): () => void {
  const startedAt = Date.now();
  let stopped = false;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const elapsed = () => Date.now() - startedAt;
  const nextDelay = () => (elapsed() < WHISP_POLL_BACKOFF_AFTER_MS ? WHISP_POLL_MS : WHISP_POLL_SLOW_MS);

  function stop(): void {
    stopped = true;
    clearTimeout(timer);
  }

  function schedule(): void {
    if (stopped || elapsed() >= WHISP_POLL_MAX_MS) return stop();
    timer = setTimeout(poll, nextDelay());
  }

  async function poll(): Promise<void> {
    try {
      const view = await fetchView();
      failures = 0;
      if (stopped) return;
      if (view) onUpdate(view);
      if (view && isWhispSettled(whispChipState(view))) return stop();
    } catch {
      if (++failures >= MAX_CONSECUTIVE_FAILURES) {
        stop();
        onError();
        return;
      }
    }
    schedule();
  }

  schedule();
  return stop;
}
